import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
  type UserCredential,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';

// Domínio do Google Workspace que aloja a equipe interna.
// O parâmetro `hd` força o Google a só permitir contas deste workspace
// (camada 1 de defesa; camada 2 é a verificação do email no cliente; camada
// 3 é o custom claim `superAdmin` validado em rules e callables).
export const ADMIN_DOMAIN = 'etiquetachef.com';

// Intenção de login desta página (#1037): `true` entre o clique em "Entrar com
// Google" e o AUTHORIZED que ele produz. Módulo, não estado React — vive o
// tempo da página, e recarga a zera, que é exatamente o que se quer.
let signInIntent = false;

/** Provider do Google preso ao Workspace (`hd`) e sempre com seletor de conta. */
function buildProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ hd: ADMIN_DOMAIN, prompt: 'select_account' });
  provider.addScope('email');
  provider.addScope('profile');
  return provider;
}

/**
 * Abre o popup de login do Google restrito ao Workspace da equipe.
 *
 * Origem: sem issue — commit 88be39195, 2026-04-26 (Sprint 2, Google SSO
 * restrito).
 *
 * Contrato: resolve com o `UserCredential`; rejeita com `FirebaseError`
 * (popup fechado, bloqueado, rede). O veredito de acesso NÃO sai daqui: o
 * `AuthProvider` reavalia no `onIdTokenChanged`.
 *
 * Usado por: `routes/Login.tsx`.
 *
 * Dicas: `hd` é só uma dica para o Google — conta pessoal ainda pode passar
 * em alguns fluxos; por isso `evaluateAdmin` confere o domínio de novo.
 *
 * Histórico:
 *  - 2026-10-26 21:15 +0100 — (#1037) marca a intenção de login para o
 *    `AuthProvider` emitir `login` só em entrada de verdade.
 */
export async function signInWithGoogle(): Promise<UserCredential> {
  // Marcado ANTES do popup: o `onIdTokenChanged` pode emitir o usuário novo
  // antes de o `signInWithPopup` resolver, e o `AuthProvider` precisa já ver a
  // intenção quando chegar ao AUTHORIZED (#1037).
  signInIntent = true;
  try {
    return await signInWithPopup(auth, buildProvider());
  } catch (err) {
    signInIntent = false;
    throw err;
  }
}

/**
 * Houve sign-in por popup nesta página desde a última vez que alguém
 * perguntou? Devolve e zera a marca.
 *
 * Origem: #1037 — 2026-10-26 21:15 +0100.
 *
 * Por quê: o `login` saía no AUTHORIZED de cada carregamento de página — as
 * marcas do `AuthProvider` (`TokenSessionMarks`) vivem num `useRef` e nascem
 * vazias a cada recarga, então sessão restaurada e aba nova contavam como
 * login. Não dá para emitir no sucesso do popup, como no `apps/app`: aqui o
 * veredito (domínio, e-mail verificado, claim `superAdmin`) só sai depois, no
 * `AuthProvider`, e conta do Google sem acesso não é login no admin.
 *
 * Contrato: síncrono, sem efeito além de zerar a marca. A marca nasce em
 * `signInWithGoogle`, morre na falha do popup, no `consumeSignInIntent` e no
 * `clearSignInIntent` (SIGNED_OUT).
 *
 * Usado por: `auth/AuthProvider.tsx` (transição para AUTHORIZED).
 */
export function consumeSignInIntent(): boolean {
  const had = signInIntent;
  signInIntent = false;
  return had;
}

/** Descarta a intenção de login (sessão encerrada antes do AUTHORIZED, #1037). */
export function clearSignInIntent(): void {
  signInIntent = false;
}

/** Encerra a sessão do Firebase Auth; o `AuthProvider` reage pelo listener. */
export async function signOut(): Promise<void> {
  await fbSignOut(auth);
}

export type { User };
